import React, { useState, useEffect } from "react";
import DefaultButton from "../../buttons/defaultButton";
import { HiCheckCircle, HiDownload, HiRefresh } from "react-icons/hi";
import { CgSpinner } from "react-icons/cg";

const ImportProgress = ({
  importJobId,
  status,
  isComplete,
  onReset,
  onViewImported,
}) => {
  const [activityLog, setActivityLog] = useState([
    { time: new Date(), message: "Import job started", type: "info" },
  ]);

  useEffect(() => {
    if (status) {
      const newLog = {
        time: new Date(),
        message: `Processed ${status.processed} of ${status.total} rows`,
        type: "info",
      };
      setActivityLog((prev) => [...prev, newLog]);
    }
  }, [status]);

  const progressPercentage = status
    ? Math.round((status.processed / status.total) * 100)
    : 0;

  const handleDownloadFailed = () => {
    // Simulate download
    const failedRows = status?.errors || [];
    const csvContent = [
      ["Row Number", "Error Reason"],
      ...failedRows.map((e) => [e.row_number, e.reason]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `failed_rows_${importJobId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    // Simulate download
    const report = {
      import_job_id: importJobId,
      total: status?.total || 0,
      imported: status?.imported || 0,
      failed: status?.failed || 0,
      skipped: status?.skipped || 0,
      errors: status?.errors || [],
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import_report_${importJobId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-6" role="region" aria-label="Import progress">
      <div>
        <h3 className="text-lg font-semibold text-theme mb-2">
          {isComplete ? "Import Complete" : "Import in Progress"}
        </h3>
        <p className="text-sm text-textLight">
          {isComplete
            ? "Your import has been completed. Review the results below."
            : "Your import is being processed. You can navigate away and return later."}
        </p>
      </div>

      {/* Progress Card */}
      <div className="bg-white border border-borderColor rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-theme mb-1">Import Progress</p>
            <p className="text-xs text-textLight">Job ID: {importJobId}</p>
          </div>
          {!isComplete && (
            <CgSpinner className="animate-spin text-theme text-2xl" />
          )}
          {isComplete && (
            <HiCheckCircle className="text-success-600 text-2xl" />
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-textLight mb-2">
            <span>{status?.processed || 0} of {status?.total || 0} processed</span>
            <span>{progressPercentage}%</span>
          </div>
          <div className="w-full bg-primary-100 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] h-3 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
              role="progressbar"
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <p className="text-xs text-textLight mb-1">Total</p>
            <p className="text-xl font-semibold text-theme">{status?.total || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-textLight mb-1">Imported</p>
            <p className="text-xl font-semibold text-success-600">
              {status?.imported || 0}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-textLight mb-1">Failed</p>
            <p className="text-xl font-semibold text-error-600">
              {status?.failed || 0}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-textLight mb-1">Skipped</p>
            <p className="text-xl font-semibold text-warning-600">
              {status?.skipped || 0}
            </p>
          </div>
        </div>

        {/* Activity Log */}
        <div className="border-t border-borderColor pt-4">
          <p className="text-sm font-medium text-theme mb-3">Activity Log</p>
          <div className="bg-primary-50 rounded-md p-3 max-h-48 overflow-y-auto">
            <div className="space-y-2">
              {activityLog.map((log, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 text-xs text-theme"
                >
                  <span className="text-textLight">
                    {log.time.toLocaleTimeString()}
                  </span>
                  <span>{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      {isComplete && (
        <div className="flex flex-wrap gap-3">
          <DefaultButton
            variant="default"
            name="View Imported Employees"
            icon={<HiCheckCircle />}
            onClick={onViewImported}
          />
          {status?.failed > 0 && (
            <DefaultButton
              variant="outline"
              name="Download Failed Rows"
              icon={<HiDownload />}
              onClick={handleDownloadFailed}
            />
          )}
          <DefaultButton
            variant="outline"
            name="Download Import Report"
            icon={<HiDownload />}
            onClick={handleDownloadReport}
          />
          <DefaultButton
            variant="secondary"
            name="Start New Import"
            icon={<HiRefresh />}
            onClick={onReset}
          />
        </div>
      )}

      {!isComplete && (
        <div className="bg-information-50 border border-information-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <HiCheckCircle className="text-information-600 text-xl flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-information-900 mb-1">
                Background Processing
              </p>
              <p className="text-xs text-information-700">
                You can safely navigate away from this page. The import will continue
                in the background. Check the Import Jobs page to view progress.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportProgress;

